/**
 * The plan errors: one shape for every refusal, from the database guards
 * (SQLSTATE PT402 / PT403, the error as JSON in the detail) and from the edge
 * functions (the same JSON as the response body). The app turns any of them
 * into the upgrade dialog (src/lib/billing/planError.ts).
 *
 * Pure, no Deno imports: vitest imports this file too.
 */

export type PlanLimitId =
  | "companies"
  | "fioAccounts"
  | "users"
  | "aiDocumentsPerMonth"
  /** The daily ceiling on AI reads (billing_daily_ai_limit()), not a plan limit of its own. */
  | "aiDocumentsPerDay";

export type PlanError =
  | { error: "plan_limit"; limit: PlanLimitId; plan: string; used: number; max: number }
  | { error: "plan_feature"; feature: string; plan: string }
  | { error: "read_only"; plan: null };

const KINDS = new Set(["plan_limit", "plan_feature", "read_only"]);

/** A plan error, or null for anything else. */
export function asPlanError(value: unknown): PlanError | null {
  let v = value;
  if (typeof v === "string") {
    try {
      v = JSON.parse(v);
    } catch {
      return null;
    }
  }
  if (!v || typeof v !== "object") return null;
  const kind = (v as { error?: unknown }).error;
  return typeof kind === "string" && KINDS.has(kind) ? (v as PlanError) : null;
}

/** 403 for a feature the plan lacks, 402 for a limit or read-only (pay to continue). */
export function planErrorStatus(err: PlanError): 402 | 403 {
  return err.error === "plan_feature" ? 403 : 402;
}

/** A Postgres/PostgREST error raised by a billing guard, as the plan error. */
export function planErrorFromDb(
  error: { code?: string; details?: string | null } | null | undefined
): PlanError | null {
  if (!error || (error.code !== "PT402" && error.code !== "PT403")) return null;
  return asPlanError(error.details ?? null);
}

/** Why a sync or an upload was skipped, for logs and e-mail inbox entries. */
export function describePlanError(err: PlanError): string {
  switch (err.error) {
    case "read_only":
      return "plan: read-only (no active plan)";
    case "plan_feature":
      return `plan: ${err.feature} is not in ${err.plan}`;
    case "plan_limit":
      return `plan: ${err.limit} limit reached (${err.used}/${err.max} on ${err.plan})`;
  }
}

/** The plan error in a failed `supabase.functions.invoke()` (its Response is in `context`). */
export async function planErrorFromInvoke(error: unknown): Promise<PlanError | null> {
  const context = (error as { context?: unknown } | null)?.context;
  if (!(context instanceof Response)) return null;
  try {
    return asPlanError(await context.clone().json());
  } catch {
    return null;
  }
}
